import { useCallback, useMemo, useState } from "react";

import { useTableViewPagination } from "@covertree/hooks";
import { ROWS_PER_PAGE } from "@covertree/consts";

import { useGetSequoiaListItemsQuery } from "@hooks/policies/useGetSequoiaListItems";

import {
  GetQuoteFilterByStatus,
  QuoteListItemsSortBy,
  SortOrder,
} from "@covertree/agent-portal-graphql";

import { useMappedBookConversions } from "./useMappedSequoia";

const filters = [
  {
    label: GetQuoteFilterByStatus.Valid,
    value: GetQuoteFilterByStatus.Valid,
  },
  {
    label: GetQuoteFilterByStatus.Expired,
    value: GetQuoteFilterByStatus.Expired,
  },
];

const initialPagination = {
  from: "0",
  filter: filters[0].value,
  order: SortOrder.Descending,
  sortBy: QuoteListItemsSortBy.EffectiveDate,
};

const columns = [
  { headerName: "Policy ID" },
  { headerName: "Quote ID" },
  { headerName: "Insured Name" },
  { headerName: "Premium" },
  { headerName: "Address" },
  {
    headerName: "Effective Date",
    sortBy: QuoteListItemsSortBy.EffectiveDate,
  },
  { headerName: "Assigned Agent" },
  { headerName: "Assigned Agency" },
  { headerName: "Source" },
  { headerName: "Prior Insurer" },
  { headerName: "Prior Policy #" },
];

export const useGetSequoia = () => {
  const [rowsPerPage, setRowsPerPage] = useState<number>(ROWS_PER_PAGE);

  const {
    currentPage,
    from,
    order,
    sortBy,
    filter,
    onChangePage,
    onChangeSort,
    onChangeFilter,
  } = useTableViewPagination(0, rowsPerPage, initialPagination);

  const handleSortChange = useCallback(
    (nextSortBy: string) => () => {
      onChangeSort(nextSortBy);
    },
    [onChangeSort]
  );

  const queryVariables = useMemo(
    () => ({
      pagination: {
        from,
        size: rowsPerPage,
      },
      sorting: {
        sort: order,
        sortBy: sortBy as QuoteListItemsSortBy,
      },
      filter: {
        status: filter as GetQuoteFilterByStatus,
        sequoia: true,
      },
    }),
    [filter, from, order, sortBy, rowsPerPage]
  );

  const { quotes, totalCount, loading, error } =
    useGetSequoiaListItemsQuery(queryVariables);

  const bookConversions = useMappedBookConversions(
    quotes,
    filter as GetQuoteFilterByStatus
  );

  return {
    bookConversions,
    loading,
    error: !!error,
    columns,
    filters,
    order,
    sortBy,
    currentPage,
    rowsPerPage,
    totalCount,
    handleFilterChange: onChangeFilter,
    handleSortChange,
    handleChangePage: onChangePage,
    handleRowsPerPageChange: setRowsPerPage,
  };
};
